import { EventBus } from '../core/EventBus';
import { audioManager } from '../core/AudioManager';
import { MobileManager } from '../core/MobileManager';

interface ActiveBuff {
  type: string;
  duration: number;
  color: number;
  element: HTMLElement;
}

export class HUD {
  private container: HTMLElement;
  private healthBar: HTMLElement;
  private healthFill: HTMLElement;
  private ammoDisplay: HTMLElement;
  private weaponName: HTMLElement;
  private speedDisplay: HTMLElement;
  private reloadIndicator: HTMLElement;
  private scoreDisplay: HTMLElement;
  private audioIndicator: HTMLElement;
  private hitMarker: HTMLElement;
  private crosshair!: HTMLElement;
  private aimIndicator!: HTMLElement;
  private buffPanel!: HTMLElement;

  private currentHealth: number = 100;
  private maxHealth: number = 100;
  private score: number = 0;
  private hitMarkerTimeout: ReturnType<typeof setTimeout> | null = null;
  private activeBuffs: Map<string, ActiveBuff> = new Map();

  // Responsive scaling
  private scale: number = 1;

  constructor() {
    this.scale = this.calculateScale();
    this.container = this.createContainer();
    this.healthBar = this.createHealthBar();
    this.healthFill = this.healthBar.querySelector('.health-fill')!;
    this.ammoDisplay = this.createAmmoDisplay();
    this.weaponName = this.createWeaponName();
    this.createCrosshair();
    this.speedDisplay = this.createSpeedDisplay();
    this.reloadIndicator = this.createReloadIndicator();
    this.scoreDisplay = this.createScoreDisplay();
    this.audioIndicator = this.createAudioIndicator();
    this.hitMarker = this.createHitMarker();
    this.buffPanel = this.createBuffPanel();

    this.setupEventListeners();

    // Listen for window resize to update scaling
    window.addEventListener('resize', this.handleResize);
  }

  private calculateScale(): number {
    return MobileManager.getScale();
  }

  private handleResize = (): void => {
    const newScale = this.calculateScale();
    if (newScale !== this.scale) {
      this.scale = newScale;
      this.applyScaling();
    }
  };

  private applyScaling(): void {
    const s = this.scale;
    const padding = Math.round(30 * s);

    // Health bar
    this.healthBar.style.bottom = `${padding}px`;
    this.healthBar.style.left = `${padding}px`;
    this.healthBar.style.width = `${Math.round(250 * s)}px`;

    // Audio indicator
    this.audioIndicator.style.top = `${padding}px`;
    this.audioIndicator.style.left = `${padding}px`;
    this.audioIndicator.style.fontSize = `${Math.round(14 * s)}px`;

    // Ammo display
    this.ammoDisplay.parentElement!.style.bottom = `${padding}px`;
    this.ammoDisplay.parentElement!.style.right = `${padding}px`;
    this.ammoDisplay.style.fontSize = `${Math.round(32 * s)}px`;

    // Weapon name
    this.weaponName.style.bottom = `${Math.round(70 * s)}px`;
    this.weaponName.style.right = `${padding}px`;
    this.weaponName.style.fontSize = `${Math.round(14 * s)}px`;

    // Speed display (top-left, below radar)
    this.speedDisplay.style.top = `${Math.round(180 * s)}px`;
    this.speedDisplay.style.left = `${padding}px`;
    this.speedDisplay.style.fontSize = `${Math.round(24 * s)}px`;

    // Score display
    this.scoreDisplay.style.top = `${padding}px`;
    this.scoreDisplay.style.right = `${padding}px`;
    const scoreValue = this.scoreDisplay.querySelector('.score-value') as HTMLElement;
    if (scoreValue) {
      scoreValue.style.fontSize = `${Math.round(28 * s)}px`;
    }

    // Buff panel
    this.buffPanel.style.top = `${Math.round(60 * s)}px`;
    this.buffPanel.style.left = `${padding}px`;
  }

  private createAudioIndicator(): HTMLElement {
    const s = this.scale;
    const padding = Math.round(30 * s);
    const indicator = document.createElement('div');
    indicator.style.cssText = `
      position: absolute;
      top: ${padding}px;
      left: ${padding}px;
      color: #00ffff;
      font-size: ${Math.round(14 * s)}px;
      text-shadow: 0 0 5px #00ffff;
      letter-spacing: 1px;
      opacity: 0.8;
    `;
    indicator.innerHTML = '&#9835; MUSIC';  // Music note symbol
    this.container.appendChild(indicator);

    // Set initial state
    if (audioManager.isMutedState()) {
      indicator.innerHTML = '&#9835; MUTED';
      indicator.style.color = '#666666';
      indicator.style.textShadow = 'none';
    }

    return indicator;
  }

  public updateAudioIndicator(muted: boolean): void {
    if (muted) {
      this.audioIndicator.innerHTML = '&#9835; MUTED';
      this.audioIndicator.style.color = '#666666';
      this.audioIndicator.style.textShadow = 'none';
    } else {
      this.audioIndicator.innerHTML = '&#9835; MUSIC';
      this.audioIndicator.style.color = '#00ffff';
      this.audioIndicator.style.textShadow = '0 0 5px #00ffff';
    }
  }

  private createContainer(): HTMLElement {
    const container = document.createElement('div');
    container.id = 'hud';
    container.style.cssText = `
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      pointer-events: none;
      font-family: 'Segoe UI', Tahoma, sans-serif;
      z-index: 100;
    `;
    document.getElementById('ui-container')?.appendChild(container);
    return container;
  }

  private createHealthBar(): HTMLElement {
    const s = this.scale;
    const padding = Math.round(30 * s);
    const wrapper = document.createElement('div');
    wrapper.style.cssText = `
      position: absolute;
      bottom: ${padding}px;
      left: ${padding}px;
      width: ${Math.round(250 * s)}px;
    `;

    const label = document.createElement('div');
    label.textContent = 'SHIELD';
    label.style.cssText = `
      color: #00ffff;
      font-size: 12px;
      margin-bottom: 5px;
      text-shadow: 0 0 10px #00ffff;
      letter-spacing: 2px;
    `;

    const bar = document.createElement('div');
    bar.style.cssText = `
      width: 100%;
      height: 20px;
      background: rgba(0, 0, 0, 0.5);
      border: 2px solid #00ffff;
      box-shadow: 0 0 10px #00ffff, inset 0 0 10px rgba(0, 255, 255, 0.1);
    `;

    const fill = document.createElement('div');
    fill.className = 'health-fill';
    fill.style.cssText = `
      width: 100%;
      height: 100%;
      background: linear-gradient(90deg, #00ffff, #00ff88);
      box-shadow: 0 0 10px #00ffff;
      transition: width 0.3s ease;
    `;

    bar.appendChild(fill);
    wrapper.appendChild(label);
    wrapper.appendChild(bar);
    this.container.appendChild(wrapper);

    return wrapper;
  }

  private createAmmoDisplay(): HTMLElement {
    const s = this.scale;
    const padding = Math.round(30 * s);
    const display = document.createElement('div');
    display.style.cssText = `
      position: absolute;
      bottom: ${padding}px;
      right: ${padding}px;
      text-align: right;
    `;

    const ammo = document.createElement('div');
    ammo.className = 'ammo-count';
    ammo.textContent = '50 / 50';
    ammo.style.cssText = `
      color: #ff00ff;
      font-size: ${Math.round(32 * s)}px;
      font-weight: bold;
      text-shadow: 0 0 10px #ff00ff;
      letter-spacing: 2px;
    `;

    display.appendChild(ammo);
    this.container.appendChild(display);

    return ammo;
  }

  private createWeaponName(): HTMLElement {
    const s = this.scale;
    const padding = Math.round(30 * s);
    const name = document.createElement('div');
    name.textContent = 'LASER CANNON';
    name.style.cssText = `
      position: absolute;
      bottom: ${Math.round(70 * s)}px;
      right: ${padding}px;
      color: #ff00ff;
      font-size: ${Math.round(14 * s)}px;
      text-shadow: 0 0 5px #ff00ff;
      letter-spacing: 3px;
    `;
    this.container.appendChild(name);
    return name;
  }

  private createCrosshair(): void {
    // Main crosshair container (moves with aim)
    this.crosshair = document.createElement('div');
    this.crosshair.style.cssText = `
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      width: 50px;
      height: 50px;
      transition: top 0.05s ease-out;
    `;

    // Outer ring
    const ring = document.createElement('div');
    ring.style.cssText = `
      position: absolute;
      top: 50%;
      left: 50%;
      width: 30px;
      height: 30px;
      border: 2px solid #00ffff;
      border-radius: 50%;
      transform: translate(-50%, -50%);
      opacity: 0.6;
      box-shadow: 0 0 8px #00ffff;
    `;

    // Center dot
    const dot = document.createElement('div');
    dot.style.cssText = `
      position: absolute;
      top: 50%;
      left: 50%;
      width: 4px;
      height: 4px;
      background: #00ffff;
      transform: translate(-50%, -50%);
      border-radius: 50%;
      box-shadow: 0 0 6px #00ffff;
    `;

    // Crosshair lines
    const lineStyle = `
      position: absolute;
      background: #00ffff;
      opacity: 0.8;
    `;

    const topLine = document.createElement('div');
    topLine.style.cssText = lineStyle + `
      top: 0;
      left: 50%;
      width: 2px;
      height: 12px;
      transform: translateX(-50%);
    `;

    const bottomLine = document.createElement('div');
    bottomLine.style.cssText = lineStyle + `
      bottom: 0;
      left: 50%;
      width: 2px;
      height: 12px;
      transform: translateX(-50%);
    `;

    const leftLine = document.createElement('div');
    leftLine.style.cssText = lineStyle + `
      left: 0;
      top: 50%;
      width: 12px;
      height: 2px;
      transform: translateY(-50%);
    `;

    const rightLine = document.createElement('div');
    rightLine.style.cssText = lineStyle + `
      right: 0;
      top: 50%;
      width: 12px;
      height: 2px;
      transform: translateY(-50%);
    `;

    this.crosshair.appendChild(ring);
    this.crosshair.appendChild(dot);
    this.crosshair.appendChild(topLine);
    this.crosshair.appendChild(bottomLine);
    this.crosshair.appendChild(leftLine);
    this.crosshair.appendChild(rightLine);
    this.container.appendChild(this.crosshair);

    // Aim indicator (shows current pitch level on side)
    this.aimIndicator = document.createElement('div');
    this.aimIndicator.style.cssText = `
      position: absolute;
      right: 50%;
      top: 50%;
      transform: translate(80px, -50%);
      color: #00ffff;
      font-size: 12px;
      font-weight: bold;
      text-shadow: 0 0 5px #00ffff;
      opacity: 0.8;
      letter-spacing: 1px;
    `;
    this.aimIndicator.textContent = 'AIM: 0°';
    this.container.appendChild(this.aimIndicator);
  }

  public updateAimPitch(pitch: number): void {
    // Convert pitch to screen offset (positive pitch = aiming up = crosshair moves up)
    // Max offset: ~150px for full range
    const maxOffset = 150;
    const offset = -pitch * (maxOffset / 0.8); // 0.8 is max pitch

    // Update crosshair position
    this.crosshair.style.top = `calc(50% + ${offset}px)`;

    // Update aim indicator
    const degrees = Math.round((pitch * 180) / Math.PI);
    if (degrees === 0) {
      this.aimIndicator.textContent = 'AIM: 0°';
      this.aimIndicator.style.color = '#00ffff';
    } else if (degrees > 0) {
      this.aimIndicator.textContent = `AIM: ↑${degrees}°`;
      this.aimIndicator.style.color = '#00ff88';
    } else {
      this.aimIndicator.textContent = `AIM: ↓${Math.abs(degrees)}°`;
      this.aimIndicator.style.color = '#ffaa00';
    }
  }

  private createSpeedDisplay(): HTMLElement {
    const s = this.scale;
    const padding = Math.round(30 * s);
    const display = document.createElement('div');
    display.style.cssText = `
      position: absolute;
      top: ${Math.round(180 * s)}px;
      left: ${padding}px;
      color: #ff00ff;
      font-size: ${Math.round(24 * s)}px;
      font-weight: bold;
      text-shadow: 0 0 10px #ff00ff;
    `;
    display.textContent = '0 km/h';
    this.container.appendChild(display);
    return display;
  }

  private createReloadIndicator(): HTMLElement {
    const indicator = document.createElement('div');
    indicator.style.cssText = `
      position: absolute;
      top: 55%;
      left: 50%;
      transform: translateX(-50%);
      color: #ffff00;
      font-size: 16px;
      text-shadow: 0 0 10px #ffff00;
      letter-spacing: 2px;
      display: none;
    `;
    indicator.textContent = 'RELOADING...';
    this.container.appendChild(indicator);
    return indicator;
  }

  private createScoreDisplay(): HTMLElement {
    const s = this.scale;
    const padding = Math.round(30 * s);
    const display = document.createElement('div');
    display.style.cssText = `
      position: absolute;
      top: ${padding}px;
      right: ${padding}px;
      text-align: right;
    `;

    const label = document.createElement('div');
    label.textContent = 'SCORE';
    label.style.cssText = `
      color: #8000ff;
      font-size: ${Math.round(12 * s)}px;
      letter-spacing: 2px;
      text-shadow: 0 0 5px #8000ff;
    `;

    const score = document.createElement('div');
    score.className = 'score-value';
    score.textContent = '0';
    score.style.cssText = `
      color: #8000ff;
      font-size: ${Math.round(28 * s)}px;
      font-weight: bold;
      text-shadow: 0 0 10px #8000ff;
    `;

    display.appendChild(label);
    display.appendChild(score);
    this.container.appendChild(display);

    return score;
  }

  private setupEventListeners(): void {
    EventBus.on('weapon:fired', (data) => {
      this.updateAmmo(data.ammoLeft, this.getMaxAmmoFromWeapon(data.weaponType));
    });

    EventBus.on('weapon:reloading', () => {
      this.showReloadIndicator();
    });

    EventBus.on('weapon:reloaded', () => {
      this.hideReloadIndicator();
    });

    EventBus.on('weapon:switched', (data) => {
      this.updateWeapon(data.weaponType, data.ammo, data.maxAmmo);
    });

    EventBus.on('player:damaged', (data) => {
      this.currentHealth = Math.max(0, this.currentHealth - data.damage);
      this.updateHealth(this.currentHealth, this.maxHealth);
      this.flashDamage();
    });

    EventBus.on('target:destroyed', (data) => {
      this.addScore(data.points);
      this.showHitMarker(true); // Kill hit marker (stronger)
    });

    EventBus.on('target:damaged', (data) => {
      this.showHitMarker(false);
      this.showDamageNumber(data.damage);
    });

    EventBus.on('projectile:hit', () => {
      this.showHitMarker(false);
    });

    // Buff events
    EventBus.on('buff:activated', (data) => {
      this.addBuff(data.type, data.duration, data.color);
    });

    EventBus.on('buff:refreshed', (data) => {
      this.refreshBuff(data.type, data.duration);
    });

    EventBus.on('buff:expired', (data) => {
      this.removeBuff(data.type);
    });

    // Weapon unlock notification
    EventBus.on('weapon:unlocked', (data) => {
      this.showWeaponUnlock(data.weapon);
    });

    // Upgrade notifications
    EventBus.on('upgrade:applied', (data) => {
      this.showUpgradeNotification(data.type, data.newLevel, data.maxLevel);
    });

    // Score events
    EventBus.on('score:added', (data) => {
      this.addScore(data.points);
      this.showScorePopup(data.points, data.reason);
    });
  }

  private createHitMarker(): HTMLElement {
    const marker = document.createElement('div');
    marker.style.cssText = `
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      width: 30px;
      height: 30px;
      pointer-events: none;
      opacity: 0;
      transition: opacity 0.05s;
    `;

    // Create X shape for hit marker
    const line1 = document.createElement('div');
    line1.style.cssText = `
      position: absolute;
      top: 50%;
      left: 50%;
      width: 20px;
      height: 2px;
      background: #ff4444;
      transform: translate(-50%, -50%) rotate(45deg);
      box-shadow: 0 0 8px #ff4444;
    `;

    const line2 = document.createElement('div');
    line2.style.cssText = `
      position: absolute;
      top: 50%;
      left: 50%;
      width: 20px;
      height: 2px;
      background: #ff4444;
      transform: translate(-50%, -50%) rotate(-45deg);
      box-shadow: 0 0 8px #ff4444;
    `;

    marker.appendChild(line1);
    marker.appendChild(line2);
    this.container.appendChild(marker);

    return marker;
  }

  public showHitMarker(isKill: boolean = false): void {
    // Clear any existing timeout
    if (this.hitMarkerTimeout) {
      clearTimeout(this.hitMarkerTimeout);
    }

    // Update color based on kill vs hit
    const color = isKill ? '#ff0000' : '#ff4444';
    const lines = this.hitMarker.querySelectorAll('div');
    lines.forEach((line) => {
      (line as HTMLElement).style.background = color;
      (line as HTMLElement).style.boxShadow = `0 0 ${isKill ? 12 : 8}px ${color}`;
    });

    // Show marker
    this.hitMarker.style.opacity = '1';

    // Hide after delay
    this.hitMarkerTimeout = setTimeout(() => {
      this.hitMarker.style.opacity = '0';
    }, isKill ? 200 : 100);
  }

  private createBuffPanel(): HTMLElement {
    const s = this.scale;
    const padding = Math.round(30 * s);
    const panel = document.createElement('div');
    panel.style.cssText = `
      position: absolute;
      top: ${Math.round(60 * s)}px;
      left: ${padding}px;
      display: flex;
      flex-direction: column;
      gap: ${Math.round(8 * s)}px;
    `;
    this.container.appendChild(panel);
    return panel;
  }

  private addBuff(type: string, duration: number, color: number): void {
    // Remove existing buff of same type if any
    if (this.activeBuffs.has(type)) {
      this.removeBuff(type);
    }

    const colorHex = '#' + color.toString(16).padStart(6, '0');

    const buffElement = document.createElement('div');
    buffElement.style.cssText = `
      display: flex;
      align-items: center;
      gap: 10px;
      padding: 8px 12px;
      background: rgba(0, 0, 0, 0.6);
      border: 2px solid ${colorHex};
      border-radius: 4px;
      box-shadow: 0 0 10px ${colorHex}40;
    `;

    const icon = document.createElement('div');
    icon.style.cssText = `
      width: 24px;
      height: 24px;
      background: ${colorHex};
      border-radius: 4px;
      box-shadow: 0 0 8px ${colorHex};
    `;

    const info = document.createElement('div');
    info.style.cssText = `
      display: flex;
      flex-direction: column;
    `;

    const name = document.createElement('div');
    name.textContent = this.getBuffName(type);
    name.style.cssText = `
      color: ${colorHex};
      font-size: 12px;
      font-weight: bold;
      text-shadow: 0 0 5px ${colorHex};
      letter-spacing: 1px;
    `;

    const timer = document.createElement('div');
    timer.className = 'buff-timer';
    timer.textContent = `${Math.ceil(duration)}s`;
    timer.style.cssText = `
      color: #ffffff;
      font-size: 14px;
      font-weight: bold;
    `;

    info.appendChild(name);
    info.appendChild(timer);
    buffElement.appendChild(icon);
    buffElement.appendChild(info);
    this.buffPanel.appendChild(buffElement);

    const buff: ActiveBuff = {
      type,
      duration,
      color,
      element: buffElement,
    };
    this.activeBuffs.set(type, buff);

    // Start countdown
    this.startBuffCountdown(type, duration);
  }

  private refreshBuff(type: string, newDuration: number): void {
    const buff = this.activeBuffs.get(type);
    if (buff) {
      buff.duration = newDuration;
      const timer = buff.element.querySelector('.buff-timer');
      if (timer) {
        timer.textContent = `${Math.ceil(newDuration)}s`;
      }
    }
  }

  private removeBuff(type: string): void {
    const buff = this.activeBuffs.get(type);
    if (buff) {
      buff.element.remove();
      this.activeBuffs.delete(type);
    }
  }

  private startBuffCountdown(type: string, duration: number): void {
    const startTime = performance.now();

    const update = () => {
      const buff = this.activeBuffs.get(type);
      if (!buff) return;

      const elapsed = (performance.now() - startTime) / 1000;
      const remaining = Math.max(0, duration - elapsed);

      const timer = buff.element.querySelector('.buff-timer');
      if (timer) {
        timer.textContent = `${Math.ceil(remaining)}s`;

        // Flash warning when low
        if (remaining <= 5) {
          timer.classList.add('warning');
          (timer as HTMLElement).style.color = '#ff4444';
          (timer as HTMLElement).style.animation = 'buffFlash 0.5s infinite';
        }
      }

      if (remaining > 0) {
        requestAnimationFrame(update);
      }
    };

    // Add flash animation if not exists
    if (!document.getElementById('buff-flash-style')) {
      const style = document.createElement('style');
      style.id = 'buff-flash-style';
      style.textContent = `
        @keyframes buffFlash {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.3; }
        }
      `;
      document.head.appendChild(style);
    }

    requestAnimationFrame(update);
  }

  private getBuffName(type: string): string {
    switch (type) {
      case 'damage':
        return 'DAMAGE +50%';
      case 'firerate':
        return 'FIRE RATE +50%';
      case 'speed':
        return 'SPEED +50%';
      default:
        return type.toUpperCase();
    }
  }

  private showWeaponUnlock(weapon: string): void {
    const weaponNames: Record<string, string> = {
      railgun: 'RAILGUN',
      plasmaCannon: 'PLASMA CANNON',
      homingMissiles: 'HOMING MISSILES',
    };

    const notification = document.createElement('div');
    notification.style.cssText = `
      position: absolute;
      top: 30%;
      left: 50%;
      transform: translate(-50%, -50%);
      padding: 20px 40px;
      background: rgba(0, 0, 0, 0.8);
      border: 3px solid #00ff88;
      border-radius: 8px;
      box-shadow: 0 0 30px #00ff8880;
      text-align: center;
      animation: weaponUnlock 2s ease-out forwards;
    `;

    notification.innerHTML = `
      <div style="color: #00ff88; font-size: 14px; letter-spacing: 3px; margin-bottom: 5px;">
        WEAPON UNLOCKED
      </div>
      <div style="color: #ffffff; font-size: 28px; font-weight: bold; text-shadow: 0 0 20px #00ff88;">
        ${weaponNames[weapon] || weapon.toUpperCase()}
      </div>
    `;

    if (!document.getElementById('weapon-unlock-style')) {
      const style = document.createElement('style');
      style.id = 'weapon-unlock-style';
      style.textContent = `
        @keyframes weaponUnlock {
          0% { opacity: 0; transform: translate(-50%, -50%) scale(0.5); }
          20% { opacity: 1; transform: translate(-50%, -50%) scale(1.1); }
          30% { transform: translate(-50%, -50%) scale(1); }
          80% { opacity: 1; }
          100% { opacity: 0; transform: translate(-50%, -50%) scale(0.9); }
        }
      `;
      document.head.appendChild(style);
    }

    this.container.appendChild(notification);

    setTimeout(() => {
      notification.remove();
    }, 2000);
  }

  private showUpgradeNotification(type: string, level: number, maxLevel: number): void {
    const upgradeNames: Record<string, string> = {
      fireRateLevel: 'FIRE RATE',
      magazineSizeLevel: 'MAGAZINE SIZE',
      projectileSpeedLevel: 'PROJECTILE SPEED',
    };

    const notification = document.createElement('div');
    notification.style.cssText = `
      position: absolute;
      top: 25%;
      left: 50%;
      transform: translate(-50%, -50%);
      padding: 15px 30px;
      background: rgba(0, 0, 0, 0.8);
      border: 2px solid #ffd700;
      border-radius: 6px;
      box-shadow: 0 0 20px #ffd70060;
      text-align: center;
      animation: upgradeNotify 1.5s ease-out forwards;
    `;

    notification.innerHTML = `
      <div style="color: #ffd700; font-size: 12px; letter-spacing: 2px; margin-bottom: 5px;">
        UPGRADE
      </div>
      <div style="color: #ffffff; font-size: 20px; font-weight: bold; text-shadow: 0 0 10px #ffd700;">
        ${upgradeNames[type] || type}
      </div>
      <div style="color: #ffd700; font-size: 14px; margin-top: 5px;">
        LEVEL ${level} / ${maxLevel}
      </div>
    `;

    if (!document.getElementById('upgrade-notify-style')) {
      const style = document.createElement('style');
      style.id = 'upgrade-notify-style';
      style.textContent = `
        @keyframes upgradeNotify {
          0% { opacity: 0; transform: translate(-50%, -50%) translateY(20px); }
          20% { opacity: 1; transform: translate(-50%, -50%) translateY(0); }
          80% { opacity: 1; }
          100% { opacity: 0; transform: translate(-50%, -50%) translateY(-20px); }
        }
      `;
      document.head.appendChild(style);
    }

    this.container.appendChild(notification);

    setTimeout(() => {
      notification.remove();
    }, 1500);
  }

  private showScorePopup(points: number, reason: string): void {
    const popup = document.createElement('div');
    popup.style.cssText = `
      position: absolute;
      top: 80px;
      right: 30px;
      color: #8000ff;
      font-size: 16px;
      font-weight: bold;
      text-shadow: 0 0 8px #8000ff;
      animation: scorePopup 1s ease-out forwards;
      text-align: right;
    `;
    popup.innerHTML = `
      <div style="font-size: 12px; opacity: 0.8;">${reason}</div>
      <div>+${points}</div>
    `;

    if (!document.getElementById('score-popup-style')) {
      const style = document.createElement('style');
      style.id = 'score-popup-style';
      style.textContent = `
        @keyframes scorePopup {
          0% { opacity: 1; transform: translateY(0); }
          100% { opacity: 0; transform: translateY(-30px); }
        }
      `;
      document.head.appendChild(style);
    }

    this.container.appendChild(popup);

    setTimeout(() => {
      popup.remove();
    }, 1000);
  }

  public showDamageNumber(damage: number): void {
    const dmgText = document.createElement('div');
    dmgText.textContent = `-${damage}`;
    dmgText.style.cssText = `
      position: absolute;
      top: 45%;
      left: 52%;
      transform: translate(-50%, -50%);
      color: #ff6600;
      font-size: 18px;
      font-weight: bold;
      text-shadow: 0 0 8px #ff6600, 2px 2px 2px rgba(0,0,0,0.8);
      pointer-events: none;
      animation: damageFloat 0.6s ease-out forwards;
    `;

    // Add animation keyframes if not already added
    if (!document.getElementById('damage-number-style')) {
      const style = document.createElement('style');
      style.id = 'damage-number-style';
      style.textContent = `
        @keyframes damageFloat {
          0% {
            opacity: 1;
            transform: translate(-50%, -50%) scale(1.2);
          }
          100% {
            opacity: 0;
            transform: translate(-50%, -150%) scale(0.8);
          }
        }
      `;
      document.head.appendChild(style);
    }

    this.container.appendChild(dmgText);

    setTimeout(() => {
      dmgText.remove();
    }, 600);
  }

  private getMaxAmmoFromWeapon(weaponType: string): number {
    // Default values based on weapon type
    switch (weaponType) {
      case 'Laser Cannon':
        return 50;
      case 'Chain Gun':
        return 200;
      default:
        return 50;
    }
  }

  public updateHealth(current: number, max: number): void {
    this.currentHealth = current;
    this.maxHealth = max;
    const percent = (current / max) * 100;
    this.healthFill.style.width = `${percent}%`;

    // Change color based on health
    if (percent < 25) {
      this.healthFill.style.background = 'linear-gradient(90deg, #ff0000, #ff4400)';
    } else if (percent < 50) {
      this.healthFill.style.background = 'linear-gradient(90deg, #ff8800, #ffaa00)';
    } else {
      this.healthFill.style.background = 'linear-gradient(90deg, #00ffff, #00ff88)';
    }
  }

  public updateAmmo(current: number, max: number): void {
    this.ammoDisplay.textContent = `${current} / ${max}`;

    // Flash when low
    if (current <= max * 0.2) {
      this.ammoDisplay.style.color = '#ff0000';
      this.ammoDisplay.style.textShadow = '0 0 10px #ff0000';
    } else {
      this.ammoDisplay.style.color = '#ff00ff';
      this.ammoDisplay.style.textShadow = '0 0 10px #ff00ff';
    }
  }

  public updateWeapon(name: string, ammo: number, maxAmmo: number): void {
    this.weaponName.textContent = name.toUpperCase();
    this.updateAmmo(ammo, maxAmmo);
  }

  public updateSpeed(speedKmh: number): void {
    this.speedDisplay.textContent = `${Math.round(speedKmh)} km/h`;
  }

  public showReloadIndicator(): void {
    this.reloadIndicator.style.display = 'block';
  }

  public hideReloadIndicator(): void {
    this.reloadIndicator.style.display = 'none';
  }

  public addScore(points: number): void {
    this.score += points;
    const scoreValue = this.scoreDisplay.querySelector('.score-value') || this.scoreDisplay;
    scoreValue.textContent = this.score.toString();

    // Flash effect
    this.scoreDisplay.style.transform = 'scale(1.2)';
    this.scoreDisplay.style.transition = 'transform 0.1s';
    setTimeout(() => {
      this.scoreDisplay.style.transform = 'scale(1)';
    }, 100);
  }

  private flashDamage(): void {
    // Red flash overlay
    const flash = document.createElement('div');
    flash.style.cssText = `
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: radial-gradient(ellipse at center, transparent 0%, rgba(255, 0, 0, 0.3) 100%);
      pointer-events: none;
      animation: damageFlash 0.3s ease-out;
    `;

    // Add animation keyframes
    const style = document.createElement('style');
    style.textContent = `
      @keyframes damageFlash {
        0% { opacity: 1; }
        100% { opacity: 0; }
      }
    `;
    document.head.appendChild(style);

    this.container.appendChild(flash);
    setTimeout(() => {
      flash.remove();
      style.remove();
    }, 300);
  }

  public getScore(): number {
    return this.score;
  }

  public showAirTimeBonus(points: number): void {
    // Create floating text for air time bonus
    const bonus = document.createElement('div');
    bonus.textContent = `AIR TIME +${points}`;
    bonus.style.cssText = `
      position: absolute;
      top: 40%;
      left: 50%;
      transform: translate(-50%, -50%);
      color: #00ff00;
      font-size: 24px;
      font-weight: bold;
      text-shadow: 0 0 15px #00ff00, 0 0 30px #00ff00;
      letter-spacing: 3px;
      pointer-events: none;
      animation: airTimeBonus 1.5s ease-out forwards;
    `;

    // Add animation keyframes if not already added
    if (!document.getElementById('airtime-style')) {
      const style = document.createElement('style');
      style.id = 'airtime-style';
      style.textContent = `
        @keyframes airTimeBonus {
          0% {
            opacity: 1;
            transform: translate(-50%, -50%) scale(0.8);
          }
          20% {
            transform: translate(-50%, -50%) scale(1.2);
          }
          100% {
            opacity: 0;
            transform: translate(-50%, -150%) scale(1);
          }
        }
      `;
      document.head.appendChild(style);
    }

    this.container.appendChild(bonus);

    // Add to score
    this.addScore(points);

    // Remove element after animation
    setTimeout(() => {
      bonus.remove();
    }, 1500);
  }

  public destroy(): void {
    window.removeEventListener('resize', this.handleResize);
    this.container.remove();
  }
}
